import moment from 'moment'

import { NOT_SET, not_set_option } from '../utils/gms_filters'
import { MAX_WINDOW_HORIZON_DAYS, format_window_value, quick_window, window_error, window_summary } from '../utils/gms_window'

// Everything the CM and TBN group message sender pages do identically: the time window controls,
// the filter counting and matching, selection, and the per-button send feedback.
//
// The two pages are deliberate twins -- one tenant type each -- and the halves that genuinely
// differ (which endpoint a send calls, which columns exist, what a location is called) stay in the
// components. Everything here had been pasted into both, which is how the search box came to look
// at different fields on each page (FI-2169 review).
//
// Expects the component to own: items, items_full, selected_ids, user_type, message_type,
// search_filter, tag_filter, the *_filters arrays and *_options lists, driverStartTime,
// driverEndTime, window_preset, loaded_window, list_loaded, sending_ids, sent_ids, fields_all,
// and a load_data()/filter_results() pair.
export default {
  computed: {
    all_shown_selected() {
      return this.items.length > 0 && this.items.every(item => this.selected_ids.indexOf(item.recipient_id) != -1)
    },
    employment_status_count_options() {
      return this.with_counts(this.employment_status_options.concat([not_set_option()]), 'employment_status', this.status_matches, this.employment_status_filters)
    },
    empty_text() {
      if (!this.list_loaded && this.needs_window)
        return 'Pick a time window above and click Find Users.'
      if (this.items_full.length > 0)
        return 'No one matches these filters.'
      return this.message_type == 'EMAIL'
        ? 'Nobody here has an email address to write to.'
        : 'Nobody here has a phone number to text.'
    },
    job_title_count_options() {
      return this.with_counts(this.job_title_options, 'job_title', null, this.job_title_filters)
    },
    location_count_options() {
      return this.with_counts(this.location_options.concat([not_set_option()]), 'company_location', null, this.company_location_filters)
    },
    matching_count() {
      return this.matching_items().length
    },
    max_date() {
      return moment().add(MAX_WINDOW_HORIZON_DAYS, 'days').format('YYYY-MM-DD')
    },
    min_date() {
      return moment().subtract(1, 'days').format('YYYY-MM-DD')
    },
    needs_window() {
      return this.user_type.indexOf('_drivers') != -1
    },
    some_shown_selected() {
      return this.items.some(item => this.selected_ids.indexOf(item.recipient_id) != -1)
    },
    window_error() {
      return this.needs_window ? window_error(this.driverStartTime, this.driverEndTime) : ''
    },
    window_is_stale() {
      return this.needs_window && this.list_loaded &&
        this.loaded_window != this.driverStartTime + '|' + this.driverEndTime
    },
    window_summary_text() {
      return window_summary(this.driverStartTime, this.driverEndTime)
    },
  },

  methods: {
    announce(message, variant, timeout) {
      this.$emit('showAlert', message, variant, timeout)
      if (variant != 'danger')
        return
      this.$nextTick(() => {
        const banner = document.getElementById('root-alert')
        if (banner && banner.scrollIntoView)
          banner.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    async check_cols() {
      this.fields = this.fields_all
      if (this.user_type != 'booked_drivers')
          this.fields = this.fields.filter(item => (item.key != 'first_driver_start_time' && item.key != 'last_driver_end_time'))
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },
    field_matches(field_value, picked) {
      if (!field_value)
        return picked.indexOf(NOT_SET) != -1
      return picked.some(value => value != NOT_SET && field_value.toLowerCase().indexOf(value.toLowerCase()) != -1)
    },
    flash_sent(id) {
      if (this.sent_ids.indexOf(id) == -1)
        this.sent_ids.push(id)
      setTimeout(() => {
        this.sent_ids = this.sent_ids.filter(sent => sent != id)
      }, 3000)
    },
    is_quick_window(name) {
      return this.window_preset == name
    },
    is_selected(item) {
      return this.selected_ids.indexOf(item.recipient_id) != -1
    },
    is_sending(id) {
      return this.sending_ids.indexOf(id) != -1
    },
    just_sent(id) {
      return this.sent_ids.indexOf(id) != -1
    },
    matching_items(skip) {
      let list = this.items_full
      if (this.message_type == 'EMAIL')
        list = list.filter(item => (item.email && item.email.indexOf('@') != -1))
      if (this.message_type == 'SMS')
        list = list.filter(item => (item.phone))
      if (this.tag_filter != '')
        list = list.filter(item => (item.tags && item.tags.toLowerCase().indexOf(this.tag_filter.toLowerCase()) != -1))
      if (skip != 'job_title' && this.job_title_filters.length > 0)
        list = list.filter(item => this.field_matches(item.job_title, this.job_title_filters))
      if (skip != 'company_location' && this.company_location_filters.length > 0)
        list = list.filter(item => this.field_matches(item.company_location, this.company_location_filters))
      if (skip != 'employment_status' && this.employment_status_filters.length > 0)
        list = list.filter(item => this.status_matches(item.employment_status, this.employment_status_filters))
      return list
    },
    sent_message(count, name) {
      const who = name || (count == 1 ? '1 person' : count + ' people')
      return this.test_mode
        ? 'test mode: nothing was sent, but ' + who + ' would have been messaged'
        : 'message sent to ' + who
    },
    set_quick_window(name) {
      const [from, to] = quick_window(name)
      this.driverStartTime = format_window_value(from)
      this.driverEndTime = format_window_value(to)
      this.window_preset = name
    },
    start_sending(id) {
      if (this.sending_ids.indexOf(id) == -1)
        this.sending_ids.push(id)
    },
    status_matches(field_value, picked) {
      const value = (field_value || '').trim().toLowerCase()
      if (!value)
        return picked.indexOf(NOT_SET) != -1
      return picked.some(picked_value => picked_value != NOT_SET && picked_value.trim().toLowerCase() == value)
    },
    stop_sending(id) {
      this.sending_ids = this.sending_ids.filter(sending => sending != id)
    },
    toggle_all_shown(checked) {
      const shown_ids = this.items.map(item => item.recipient_id)
      const others = this.selected_ids.filter(id => shown_ids.indexOf(id) == -1)
      this.selected_ids = checked ? others.concat(shown_ids) : others
    },
    toggle_one(item) {
      if (this.is_selected(item))
        this.selected_ids = this.selected_ids.filter(id => id != item.recipient_id)
      else
        this.selected_ids = this.selected_ids.concat([item.recipient_id])
    },
    with_counts(options, field, matcher, picked) {
      const pool = this.matching_items(field)
      const matches = matcher || this.field_matches
      const checked = picked || []
      const counted = []
      for (const option of options) {
        if (option.value == '')
          continue
        const count = pool.filter(item => matches(item[field], [option.value])).length
        if (count == 0 && checked.indexOf(option.value) == -1)
          continue
        counted.push({ value: option.value, text: option.text + ' (' + count + ')' })
      }
      return counted
    },
  },

  watch: {
    company_location_filters() {
      this.filter_results()
      this.save_sticky_filters()
    },
    employment_status_filters() {
      this.filter_results()
      this.save_sticky_filters()
    },
    job_title_filters() {
      this.filter_results()
      this.save_sticky_filters()
    },
    message_type() {
      this.filter_results()
      this.save_sticky_filters()
    },
    search_filter() {
      this.filter_results(true)
    },
    tag_filter() {
      this.filter_results()
    },
    window_preset() {
      this.save_sticky_filters()
    },
  },
}
